/**
 * Create an invoice — the full-screen form (OR-1449).
 *
 * Lives outside `SubmissionLayout` on purpose: the Figma drops the section
 * nav and the submission header here, leaving only the app rail, a back
 * arrow and the form. That means this screen loads the submission itself
 * rather than reading `useSubmissionContext`.
 *
 * Nearly every field arrives pre-filled and locked — see
 * `use-invoice-prefill.ts` for where each value comes from. The user only
 * completes what Orion genuinely doesn't know: the brokerage's phone, who
 * to bill, the account manager, the due date and the coverage line.
 *
 * Premium & Fees is entirely the backend's: every figure there is a quote
 * column or a rollup it computes. Commissions is the one card the user
 * writes to, and only two fields of it — Taxes and Agency fee — which
 * reach the invoice through the draft (Generate) or a PATCH (Edit).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, XCircle } from "@untitledui/icons";
import { Badge, Button, Input, Label, Select, cx } from "@tricura/atlas-ui";
import {
    ApiError,
    COVERAGE_TYPE_LABELS,
    createAscendInvoice,
    deleteInvoiceDraft,
    getInvoiceDraft,
    listAllAscendInvoices,
    patchSubmission,
    putInvoiceDraft,
    updateAscendInvoice,
    waitForAscendInvoiceSync,
    type AscendCoverageType,
    type AscendInvoice,
    type CreateAscendInvoiceRequest,
} from "../../../api";
import { DateTextField } from "../../../components/date-text-field";
import { FeaturedAlert } from "../../../components/featured-alert";
import { FloatingAlert } from "../../../components/floating-alert";
import { FormGroup } from "../../../components/form-group";
import { LockedField } from "../../../components/locked-field";
import { CharCountHint, isAtLimit } from "../../../components/char-count-hint";
import { SUBMISSION_TEXT_MAX_LENGTH } from "../../../utils/field-limits";
import { MoneyInput } from "../../../components/money-input";
import { OrionSidebar } from "../../../components/orion-sidebar";
import { WarningDialog } from "../../../components/warning-dialog";
import { useSubmissionDetail } from "./use-submission-detail";
import { useInvoicePrefill, type InvoicePrefill } from "./use-invoice-prefill";
import { InvoiceSummary } from "./invoice-summary";
import { INVOICE_CARD_TOOLTIPS } from "./invoice-tooltips";
import { invoiceBadge } from "./invoice-status";
import { errorCount, validateInvoiceForm, type InvoiceErrors } from "./invoice-validation";
import { billingUserNameFor, type BillingUserName } from "./billing-user-name";
import { generateFailureMessage, needsAccountPayload } from "./generate-invoice-error";

/** Two-up field grid, the form's default row. */
function Row({ children, cols = 2 }: { children: React.ReactNode; cols?: 2 | 3 }) {
    return <div className={cx("grid grid-cols-1 gap-5", cols === 2 ? "md:grid-cols-2" : "md:grid-cols-3")}>{children}</div>;
}

function formatMoney(value: number | null, fallback = ""): string {
    if (value == null) return fallback;
    return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** A locked money value, shown the way the read-only cards need it. */
function LockedMoney({
    label,
    value,
    hint,
}: {
    label: string;
    value: number | null;
    /** Validation message for a locked figure the form still depends on. */
    hint?: string;
}) {
    return <LockedField label={label} value={formatMoney(value)} placeholder="—" isInvalid={Boolean(hint)} hint={hint} />;
}

/**
 * One of the two money figures the user owns, collapsing to the locked
 * look in view mode like every other field on this form.
 */
function EditableMoney({
    readOnly,
    label,
    value,
    onChange,
    autotest,
}: {
    readOnly: boolean;
    label: string;
    value: string;
    onChange: (raw: string) => void;
    autotest: string;
}) {
    if (readOnly) return <LockedMoney label={label} value={toMoneyNumber(value)} />;
    return (
        <div className="flex w-full flex-col gap-1.5">
            <Label>{label}</Label>
            <MoneyInput
                size="sm"
                value={value}
                onChange={onChange}
                aria-label={label}
                data-autotest={autotest}
            />
        </div>
    );
}

/** The grey full-bleed total strip that closes the money cards. */
function TotalRow({ label, value }: { label: string; value: number | null }) {
    return (
        <div className="bg-secondary flex items-center justify-between rounded-lg px-4 py-3">
            <span className="text-secondary text-sm">{label}</span>
            <span className="text-primary text-sm font-semibold tabular-nums">{formatMoney(value, "—")}</span>
        </div>
    );
}

const COVERAGE_ITEMS = (Object.keys(COVERAGE_TYPE_LABELS) as AscendCoverageType[]).map((id) => ({
    id,
    label: COVERAGE_TYPE_LABELS[id],
}));

/**
 * Not data — contract constants, named here so nothing reads as a magic
 * string:
 *
 * Billing type is system-set by design (OR-1497: "mock shows this
 * locked/system-set, which is correct... FE is sending agency bill even
 * though it's a locked field because the BE requires it"). Orion never
 * uses direct billing, so the wire value is always `"agency"` and the
 * display label always "Agency bill".
 *
 * The Commissions group heading is the Figma's literal copy for the
 * wholesaler side of the split. The backend's counterpart is a config
 * value (ASCEND_WHOLESALER_IDENTIFIER) the API does not expose — on the
 * ask list; if it ever does, read it from there instead.
 */
const BILLING_TYPE_WIRE = "agency" as const;
const BILLING_TYPE_LABEL = "Agency bill";
const WHOLESALER_GROUP_LABEL = "Tricura Insurance Group";
/** Carrier is a static backend config value Ascend consumes in a wire
 *  format not meant for display; per Sammy (Slack, 2026-09-09) the FE
 *  should show this literal, which is also the mocks' copy and the name
 *  printed on the real Ascend PDF. */
const CARRIER_LABEL = "Accelerant Specialty Insurance Company";

/** A MoneyInput's raw string → number, treating blank and junk as absent. */
function toMoneyNumber(raw: string): number | null {
    if (raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/**
 * `view` reuses this same screen with every control locked and the action
 * buttons gone — the design is explicit that it should not be a separate
 * template. `edit` is the Draft-only variant whose footer says Save
 * Changes instead of Generate.
 */
export type InvoiceFormMode = "create" | "edit" | "view";

/** An `Input` that collapses to a `LockedField` in view mode. */
function TextField({
    readOnly,
    label,
    value,
    ...rest
}: {
    readOnly: boolean;
    label: string;
    value: string;
    isRequired?: boolean;
    placeholder?: string;
    onChange?: (v: string) => void;
    isInvalid?: boolean;
    hint?: string;
    maxLength?: number;
    "data-autotest"?: string;
}) {
    if (readOnly) return <LockedField label={label} value={value} />;
    return <Input size="sm" label={label} value={value} {...rest} />;
}

/** `YYYY-MM-DD` → `MM/DD/YYYY`, without a Date round trip (no TZ shifts). */
function isoToUs(iso: string): string {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return y && m && d ? `${m}/${d}/${y}` : "";
}

/** Wire fraction (`0.1084`) → display percent (`10.84`), or null. */
function rateToPct(rate: number | null | undefined): number | null {
    return rate == null ? null : Math.round(rate * 10000) / 100;
}

type SubmitState = { status: "idle" } | { status: "working" } | { status: "failed"; message: string };

export function CreateInvoicePage({ mode = "create" }: { mode?: InvoiceFormMode } = {}) {
    const { id: submissionId = "", invoiceId } = useParams();
    const readOnly = mode === "view";
    const navigate = useNavigate();
    const detailState = useSubmissionDetail(submissionId);
    const detail = detailState.status === "ready" ? detailState.detail : null;
    const prefillState = useInvoicePrefill(detail);
    const prefill = prefillState.status === "ready" ? prefillState.prefill : null;

    // What the user actually fills in.
    const [phone, setPhone] = useState("");
    /** The loaded row in View/Edit; null while creating. Declared up here
     *  because the validation memo below reads its billed premium. */
    const [invoice, setInvoice] = useState<AscendInvoice | null>(null);
    const [billingBrokerId, setBillingBrokerId] = useState<string | null>(null);
    // Pre-filled from the chosen broker but owned by the user from then on:
    // a broker row can carry a display name and still have null
    // first_name/last_name (OR-1555), and sending "" to Ascend fails the
    // whole create with an opaque ERR_VALIDATION. The underwriter has to be
    // able to see and fix the name before it leaves the form.
    const [billingFirstName, setBillingFirstName] = useState("");
    const [billingLastName, setBillingLastName] = useState("");
    const [accountManager, setAccountManager] = useState("");
    const [accountEmail, setAccountEmail] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [coverageType, setCoverageType] = useState<AscendCoverageType | null>(null);
    // The only two money figures the user owns. Raw decimal strings, as
    // MoneyInput and the API both want them.
    const [tricuraTax, setTricuraTax] = useState("");
    const [agencyFee, setAgencyFee] = useState("");

    const [dirty, setDirty] = useState(false);
    // Errors stay hidden until the first Generate attempt, so a form the
    // user is still filling in isn't already shouting at them.
    const [showErrors, setShowErrors] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [submit, setSubmit] = useState<SubmitState>({ status: "idle" });
    const [dismissedBanners, setDismissedBanners] = useState<Record<string, boolean>>({});
    const scrollRef = useRef<HTMLDivElement>(null);
    // Mirrors `dirty` for async callbacks: the draft restore below resolves
    // after the first render and must never stomp something the user has
    // already typed or a broker they have already switched to.
    const dirtyRef = useRef(false);

    /**
     * Select a billing broker and seed the editable name from it, unless a
     * confirmed name is handed in (a restored draft). The only three places
     * the broker changes all go through here, so the name can never drift
     * from the broker it belongs to. A broker with null first/last name
     * gets them derived from its display name, the same split the backend
     * applies, so what the user sees is what would be sent.
     */
    const selectBillingBroker = useCallback(
        (id: string | null, confirmed?: BillingUserName) => {
            const broker = prefill?.brokers.find((b) => b.id === id) ?? null;
            const name = billingUserNameFor(broker, confirmed);
            setBillingBrokerId(id);
            setBillingFirstName(name.first);
            setBillingLastName(name.last);
        },
        [prefill],
    );

    // Prefill seeds the editable fields once, then gets out of the way so
    // it can never stomp something the user has typed.
    const [seeded, setSeeded] = useState(false);
    if (prefill && !seeded) {
        setSeeded(true);
        setPhone(prefill.brokeragePhone);
        selectBillingBroker(prefill.defaultBrokerId);
        setAccountManager(prefill.accountManagerName);
        setAccountEmail(prefill.accountManagerEmail);
    }

    // A saved draft re-opens with its persisted choice. Runs once the form is
    // seeded (an effect, not the render above, because it reads `dirtyRef`).
    // The form stays in its loading state until this settles, so the user
    // cannot edit or Save ahead of the restore and overwrite the draft with
    // defaults. A 404 is simply "no draft" and opens an empty form; any other
    // failure keeps the form closed behind a retry, because a Save issued
    // over a draft the page never read would silently replace it.
    const [draftRead, setDraftRead] = useState<"loading" | "ready" | "error">(
        mode === "create" ? "loading" : "ready",
    );
    const [draftAttempt, setDraftAttempt] = useState(0);
    useEffect(() => {
        if (!seeded || !prefill || mode !== "create") return;
        let cancelled = false;
        setDraftRead("loading");
        void getInvoiceDraft(submissionId)
            .then((draft) => {
                if (cancelled) return;
                setDraftRead("ready");
                // A slow response must not undo anything the user touched in
                // the meantime.
                if (!draft || dirtyRef.current) return;
                if (draft.coverageType) setCoverageType(draft.coverageType);
                // The billing user the draft was saved with: re-select that
                // broker (it may differ from the default) and keep the names
                // the user confirmed.
                const user = draft.accountUserPayload;
                const savedBroker = user
                    ? prefill.brokers.find((b) => b.email?.toLowerCase() === user.email.toLowerCase())
                    : undefined;
                if (user && savedBroker) {
                    selectBillingBroker(savedBroker.id, {
                        first: user.first_name ?? "",
                        last: user.last_name ?? "",
                    });
                }
                if (draft.tricuraCommissionTaxAmount != null) setTricuraTax(String(draft.tricuraCommissionTaxAmount));
                if (draft.agencyFeeAmount != null) setAgencyFee(String(draft.agencyFeeAmount));
            })
            .catch(() => {
                if (!cancelled) setDraftRead("error");
            });
        return () => {
            cancelled = true;
        };
    }, [seeded, prefill, mode, submissionId, selectBillingBroker, draftAttempt]);

    /** Every setter goes through here so the discard prompt is accurate. */
    function edit<T>(setter: (v: T) => void) {
        return (value: T) => {
            dirtyRef.current = true;
            setDirty(true);
            setter(value);
        };
    }

    const billingBroker = useMemo(
        () => prefill?.brokers.find((b) => b.id === billingBrokerId) ?? null,
        [prefill, billingBrokerId],
    );

    const errors: InvoiceErrors = useMemo(
        () =>
            validateInvoiceForm({
                // The guard runs on the premium the backend bills: the row's
                // own snapshot once it exists, the quote's before that.
                premium: (() => {
                    const p = invoice?.selectedPremium ?? prefill?.selectedPremium;
                    return p == null ? "" : String(p);
                })(),
                phone,
                billingBrokerId,
                billingBrokerEmail: billingBroker?.email ?? null,
                billingFirstName,
                billingLastName,
                accountManager,
                accountEmail,
                dueDate,
                coverageType,
            }),
        [
            prefill,
            invoice?.selectedPremium,
            phone,
            billingBrokerId,
            billingBroker,
            billingFirstName,
            billingLastName,
            accountManager,
            accountEmail,
            dueDate,
            coverageType,
        ],
    );
    const err = (field: string) => (showErrors ? errors[field] : undefined);

    const brokerItems = useMemo(
        () =>
            (prefill?.brokers ?? []).map((b) => ({
                id: b.id,
                label: b.email || b.name || "—",
            })),
        [prefill],
    );

    const brokerageAddressLine = useMemo(
        () =>
            !prefill
                ? ""
                : [prefill.brokerageStreet, prefill.brokerageCity, prefill.brokerageState, prefill.brokerageZip]
                      .filter((p) => p && p.trim() !== "")
                      .join(", "),
        [prefill],
    );

    const insuredAddressLine = useMemo(
        () =>
            !prefill
                ? ""
                : [prefill.fniStreet, prefill.fniCity, prefill.fniState, prefill.fniZip]
                      .filter((p) => p && p.trim() !== "")
                      .join(", "),
        [prefill],
    );

    /**
     * NO money arithmetic happens on this screen. Every figure shown is a
     * value the backend exposes (quote columns, or the invoice row after
     * the sync); anything the backend does not expose renders as an em
     * dash and sits on the ask list. PR #367 changing the surcharge
     * composition mid-flight is exactly why: the backend owns the math.
     */
    /**
     * View and Edit are reached from a row in Invoice History, so the
     * invoice itself has to come back. There is no single-invoice GET, so
     * this pulls the submission's list and picks it out — the same reason
     * `waitForAscendInvoiceSync` takes a submission id.
     */
    useEffect(() => {
        if (mode === "create" || !invoiceId || !submissionId) return;
        let cancelled = false;
        listAllAscendInvoices(submissionId)
            .then((res) => {
                if (cancelled) return;
                const row = res.items.find((i) => i.id === invoiceId) ?? null;
                setInvoice(row);
                // The row is what was invoiced — View/Edit start from it.
                if (row) {
                    setCoverageType(row.coverageType);
                    if (row.tricuraCommissionTaxAmount != null)
                        setTricuraTax(String(row.tricuraCommissionTaxAmount));
                    if (row.agencyFeeAmount != null) setAgencyFee(String(row.agencyFeeAmount));
                }
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [mode, invoiceId, submissionId]);

    const summary = useMemo(
        () => ({
            // Server-generated `{brokerage}-{sequence}` — nothing to show
            // until the invoice exists, but once it does (View/Edit) the
            // backend's own number and due date take over.
            invoiceNumber: invoice?.invoiceNumber ?? "",
            dueDate: invoice?.dueDate ? isoToUs(invoice.dueDate) : dueDate,
            billToName: prefill?.brokerageName ?? "",
            billToEmail: billingBroker?.email ?? "",
            billToAddress: brokerageAddressLine,
            insuredName: prefill?.fni ?? "",
            insuredAddress: insuredAddressLine,
            // Once a row exists its billed values win over the live
            // submission/quote (backend PR #415, OR-1657): a correction
            // must show, and a quote edit after invoicing must not.
            policyNumber: invoice?.policyNumber ?? prefill?.policyNumber ?? "",
            coverageType,
            effectiveDate: invoice?.effectiveDate ? isoToUs(invoice.effectiveDate) : (prefill?.effectiveDate ?? ""),
            expirationDate: invoice?.expirationDate ? isoToUs(invoice.expirationDate) : (prefill?.expirationDate ?? ""),
            selectedPremium: invoice?.selectedPremium ?? prefill?.selectedPremium ?? null,
            // The row's rollups are the billed figures (backend PR #383) and
            // win wherever a row exists; before one does, only the quote's
            // own columns are available.
            taxes: invoice?.taxesTotal ?? prefill?.taxesAmount ?? null,
            surcharges: invoice?.surchargesTotal ?? prefill?.surchargesAmount ?? null,
            policyFee: prefill?.policyFee ?? null,
            brokerageCommission: invoice?.agentCommissionAmount ?? null,
            // The invoiced rate (4 dp, what Ascend bills) rather than the
            // quote's, so the amount beside it always matches.
            brokerageCommissionPct: rateToPct(invoice?.agentCommissionRate) ?? prefill?.agentCommissionPct ?? null,
            // The backend computes the invoice total; before the sync there
            // is nothing to show but the dash.
            dueValue: mode === "view" ? (invoice?.dueValue ?? null) : null,
        }),
        [prefill, billingBroker, brokerageAddressLine, insuredAddressLine, dueDate, coverageType, mode, invoice],
    );

    /**
     * The two "detected a mismatch" banners from the Figma.
     *
     * ⚠️ Both compare a value the user typed on this form against the page
     * that owns it — and today neither premium nor policy number is
     * editable here, so the comparison can never differ and the banners
     * cannot appear. They are wired rather than deleted because the moment
     * the backend accepts a premium override the condition becomes real.
     * Flagged in the gap list.
     */
    // Dormant: the premium is no longer editable here (the backend owns
    // every calculation), so there is no second value to mismatch against.
    // Wired so the banner lights up if an editable premium ever returns.
    const premiumMismatch = false;
    // No second source to compare against yet — the invoice's policy number
    // is read-only and comes from Policy Details, so it cannot diverge.
    // Wired so the banner lights up the day that changes.
    const policyMismatch = false;

    const showPremiumMismatch = premiumMismatch && !dismissedBanners.premium;
    const showPolicyMismatch = policyMismatch && !dismissedBanners.policy;

    const goInvoices = useCallback(() => navigate(`/submissions/${submissionId}/invoice`), [navigate, submissionId]);

    const onBack = () => {
        if (dirty) setDiscardOpen(true);
        else goInvoices();
    };

    const onGenerateClick = () => {
        setShowErrors(true);
        if (errorCount(errors) > 0) {
            // The error toast sits at the top of the form, and Generate lives
            // in the footer — so from anywhere below the fold the user would
            // be told nothing at all. Design calls this out explicitly.
            scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        setConfirmOpen(true);
    };

    /**
     * Builds the Ascend Account half of the request.
     *
     * The candidates/link discovery flow was removed from the backend
     * (sep-04): every Ascend account already matches an Orion brokerage,
     * so the backend check-then-creates internally. The FE's only job is
     * supplying `account_payload` for a brokerage's FIRST invoice — the
     * backend rejects the bare create with a 422 naming account_payload
     * when it is needed, and this fills it from the form.
     */
    /**
     * The billing user as Ascend should know them. Sent on EVERY create and
     * saved with the draft: Ascend only learns a person's name when it
     * provisions the Account User, and the backend needs this payload the
     * first time a broker is invoiced even when the Account already exists
     * (its own 422 for that case is not the account_payload one the retry
     * below catches). Omitted when the broker has no email, since the
     * backend validates it as an address and would refuse the whole body.
     */
    const accountUserPayload = useCallback((): Pick<CreateAscendInvoiceRequest, "account_user_payload"> => {
        const email = billingBroker?.email?.trim();
        if (!email) return {};
        return {
            account_user_payload: {
                email,
                first_name: billingFirstName.trim(),
                last_name: billingLastName.trim(),
                role: "producer",
            },
        };
    }, [billingBroker, billingFirstName, billingLastName]);

    const buildAccountPayload = useCallback((): Partial<CreateAscendInvoiceRequest> => {
        if (!prefill) return {};
        const first = billingBroker;
        return {
            account_payload: {
                organization_account: {
                    contact: {
                        first_name: billingFirstName.trim(),
                        last_name: billingLastName.trim(),
                        email: first?.email ?? "",
                    },
                    agency: {
                        name: prefill.brokerageName,
                        phone,
                        mailing_address: {
                            street_one: prefill.brokerageStreet,
                            city: prefill.brokerageCity,
                            state: prefill.brokerageState,
                            zip_code: prefill.brokerageZip,
                        },
                    },
                    // Unit conversion only (the API takes a fraction, the
                    // quote stores a percent) — not business math.
                    default_seller_commission_rate: (prefill.agentCommissionPct ?? 0) / 100,
                    billing_type: BILLING_TYPE_WIRE,
                },
            },
        };
    }, [prefill, billingBroker, billingFirstName, billingLastName, phone]);

    /**
     * `total_commission_amount` is the one figure the API expects the FE to
     * arrive at: the backend stores it verbatim instead of deriving it, a
     * deliberate choice documented on its model. The formula is theirs, not
     * ours — Wholesaler/MGA fee + Brokerage commission + Taxes + Agency fee,
     * per the field's own description — so this reproduces it rather than
     * inventing any accounting.
     *
     * ⚠️ The two commission figures only exist on an invoice ROW, and on the
     * create screen there is no row yet — the total is nevertheless persisted
     * at that very moment, through the draft. So before a row exists they are
     * re-derived here from the quote by the backend's own published rule
     * (rate × selected premium, the exact wording of those fields' API
     * descriptions). Without it we would persist a total missing its two
     * largest terms, and the create screen would contradict View.
     *
     * Null while nothing is known: sending a lone 0 would persist "the
     * commissions total nothing", which is a claim, not an absence.
     */
    const totalCommission = useMemo(() => {
        const fromRate = (pct: number | null | undefined) =>
            pct == null || prefill?.selectedPremium == null ? null : (pct / 100) * prefill.selectedPremium;
        const parts = [
            invoice?.tricuraCommissionAmount ?? fromRate(prefill?.tricuraCommissionPct),
            invoice?.agentCommissionAmount ?? fromRate(prefill?.agentCommissionPct),
            toMoneyNumber(tricuraTax),
            toMoneyNumber(agencyFee),
        ];
        return parts.some((p) => p != null) ? parts.reduce<number>((sum, p) => sum + (p ?? 0), 0) : null;
    }, [invoice, prefill, tricuraTax, agencyFee]);

    /** The three writable money fields, shaped for the draft PUT / PATCH. */
    const moneyPayload = useCallback(
        () => ({
            tricura_commission_tax_amount: tricuraTax === "" ? null : tricuraTax,
            agency_fee_amount: agencyFee === "" ? null : agencyFee,
            total_commission_amount: totalCommission == null ? null : String(totalCommission),
        }),
        [tricuraTax, agencyFee, totalCommission],
    );

    /**
     * Save the account manager onto the submission, if the user changed it.
     *
     * Skipped when it already matches what prefill loaded — this screen is
     * about invoicing, so it should not issue a submission write it has no
     * reason to.
     */
    const persistAccountManager = useCallback(async () => {
        if (!prefill) return;
        if (accountManager === prefill.accountManagerName && accountEmail === prefill.accountManagerEmail) return;
        try {
            await patchSubmission(submissionId, {
                account_manager_name: accountManager || null,
                account_manager_email: accountEmail || null,
            });
        } catch {
            // Deliberately swallowed — see the call site.
        }
    }, [prefill, accountManager, accountEmail, submissionId]);

    /**
     * Edit mode's submit: the money-only PATCH.
     *
     * The backend applies the correction atomically and answers with the
     * corrected invoice; any failure arrives as a real HTTP status and lands
     * in the catch below, so a 200 means everything landed.
     */
    const onSaveChanges = useCallback(async () => {
        if (!invoiceId) return;
        setSubmit({ status: "working" });
        try {
            await updateAscendInvoice(invoiceId, moneyPayload());
            navigate(`/submissions/${submissionId}/invoice`);
        } catch (e: unknown) {
            setSubmit({
                status: "failed",
                message:
                    e instanceof ApiError && e.status === 409
                        ? "This invoice is archived and can no longer be edited."
                        : e instanceof Error
                          ? e.message
                          : "Couldn't save the changes.",
            });
        }
    }, [invoiceId, moneyPayload, navigate, submissionId]);

    const onConfirmGenerate = useCallback(async () => {
        setConfirmOpen(false);
        if (!coverageType) return;
        setSubmit({ status: "working" });

        // Invoices are a flat resource now — the submission travels in the
        // body, not the path.
        const body: CreateAscendInvoiceRequest = {
            submission_id: submissionId,
            coverage_type: coverageType,
            ...accountUserPayload(),
        };
        try {
            // The create body has no room for money, so the draft is the only
            // way in: `finalize_draft()` flips this very row to `pending`
            // without touching the three money columns, so saving first is
            // what carries Taxes and Agency fee onto the real invoice.
            await putInvoiceDraft(submissionId, {
                coverage_type: coverageType,
                ...accountUserPayload(),
                ...moneyPayload(),
            }).catch(() => undefined);

            // Account manager is a SUBMISSION field (the same one UW
            // Information edits) that this form pre-fills from and lets the
            // user correct — but no invoice payload carries it. Writing it
            // back is what keeps the correction: otherwise the two required
            // fields silently discard whatever was typed, and the next
            // invoice asks for them again. Best-effort: a failure here must
            // not sink an otherwise good invoice.
            void persistAccountManager();

            let invoice;
            try {
                invoice = await createAscendInvoice(body);
            } catch (e: unknown) {
                // The one recoverable failure: this brokerage has never been
                // invoiced, so Ascend has no Account for it yet. Always
                // sending account_payload is not an option: the backend
                // validates it on every request, so an incomplete brokerage
                // record would newly block already-linked ones.
                if (!needsAccountPayload(e)) throw e;
                invoice = await createAscendInvoice({ ...body, ...buildAccountPayload() });
            }

            // 202: the row exists but Ascend hasn't seen it yet. A timeout is
            // not a failure — the worker keeps going — so either way the user
            // lands on the list, where the row is already visible.
            const settled = await waitForAscendInvoiceSync(submissionId, invoice.id, { timeoutMs: 45_000 });
            if (settled !== "timeout" && settled.syncStatus === "failed") {
                // The row exists but Ascend rejected the sync — celebrating
                // here would be lying. The draft is deliberately kept, so
                // nothing the user chose is lost.
                setSubmit({
                    status: "failed",
                    message:
                        "The sync to Ascend didn't complete. Nothing you entered is lost — try generating again.",
                });
                return;
            }
            // Only now is the draft truly consumed: the invoice is real, so a
            // lingering draft would just render a stale Draft row beside it.
            void deleteInvoiceDraft(submissionId).catch(() => undefined);
            navigate(`/submissions/${submissionId}/invoice`, {
                state: { generatedInvoiceNumber: invoice.invoiceNumber },
            });
        } catch (e: unknown) {
            // A 409 (Account relink refused, number collision, concurrent
            // edit) lands here too: the form and the draft stay as they
            // are, so the user can generate again.
            setSubmit({ status: "failed", message: generateFailureMessage(e) });
        }
    }, [
        coverageType,
        submissionId,
        accountUserPayload,
        moneyPayload,
        persistAccountManager,
        buildAccountPayload,
        navigate,
    ]);

    const headerTitle =
        mode === "create"
            ? "Create an invoice"
            : mode === "edit"
              ? "Edit"
              : // View's title is the invoice's own identity, per the design.
                [invoice?.invoiceNumber, invoice?.namedInsured].filter(Boolean).join(" - ") || "Invoice";

    const headerBadge = useMemo(() => {
        if (mode !== "view") return { label: "Draft", color: "gray" as const };
        // Draft is a sync state, not a fallback: a generated invoice whose
        // Ascend status hasn't been captured (in-flight, or a failed sync)
        // shows NO badge, same as its table row — the approved set has no
        // label for that window.
        if (invoice?.syncStatus === "draft") return { label: "Draft", color: "gray" as const };
        return invoiceBadge(invoice?.ascendInvoiceStatus ?? null);
    }, [mode, invoice]);

    const loading = detailState.status === "loading" || prefillState.status === "loading" || draftRead === "loading";
    const loadError =
        detailState.status === "error"
            ? detailState.message
            : prefillState.status === "error"
              ? prefillState.message
              : null;
    const draftReadFailed = draftRead === "error";
    const working = submit.status === "working";

    return (
        <div className="bg-secondary flex min-h-screen flex-col lg:h-screen lg:flex-row">
            <OrionSidebar />

            <div className="bg-primary flex flex-1 flex-col overflow-hidden">
                <header className="border-secondary bg-primary flex shrink-0 items-center gap-3 border-b px-4 py-4 sm:px-6">
                    <button
                        type="button"
                        onClick={onBack}
                        aria-label="Back to invoices"
                        className="text-fg-quaternary hover:text-fg-secondary hover:bg-secondary focus-visible:ring-focus-ring inline-flex size-8 cursor-pointer items-center justify-center rounded outline-none focus-visible:ring-2"
                        data-autotest="invoice-create-back"
                    >
                        <ArrowLeft className="size-5" />
                    </button>
                    <h1 className="text-primary text-md font-semibold" data-autotest="invoice-form-title">
                        {headerTitle}
                    </h1>
                    {headerBadge && (
                        <Badge color={headerBadge.color} type="pill-color" size="sm">
                            {headerBadge.label}
                        </Badge>
                    )}
                </header>

                {/* `relative` gives the floating alerts something to anchor to. */}
                <div ref={scrollRef} className="bg-secondary relative flex-1 overflow-y-auto p-4 sm:p-6">
                    {loadError && (
                        <div role="alert" className="text-error-primary pb-4 text-sm">
                            Couldn't load this submission: {loadError}
                        </div>
                    )}
                    {draftReadFailed && (
                        <div role="alert" className="text-error-primary flex items-center gap-3 pb-4 text-sm">
                            <span>Couldn't load the saved invoice draft.</span>
                            <Button
                                size="sm"
                                color="secondary"
                                onClick={() => setDraftAttempt((n) => n + 1)}
                                data-autotest="invoice-draft-retry"
                            >
                                Retry
                            </Button>
                        </div>
                    )}
                    {loading && <div className="text-tertiary text-sm">Loading submission…</div>}

                    {!loading && !draftReadFailed && prefill && (
                        <div className="flex flex-col gap-4">
                            {/* Outcome messages float centred over the form;
                              * the mismatch banners below stay in the flow. */}
                            {((showErrors && errorCount(errors) > 0) || submit.status === "failed") && (
                                <FloatingAlert>
                                    {showErrors && errorCount(errors) > 0 && (
                                        <FeaturedAlert
                                            color="error"
                                            icon={XCircle}
                                            className="shadow-lg"
                                            title={`${errorCount(errors)} required ${errorCount(errors) === 1 ? "field is" : "fields are"} empty`}
                                            description="Complete the fields marked below to generate the invoice."
                                            onClose={() => setShowErrors(false)}
                                            data-autotest="invoice-required-alert"
                                        />
                                    )}
                                    {submit.status === "failed" && (
                                        <FeaturedAlert
                                            color="error"
                                            icon={XCircle}
                                            className="shadow-lg"
                                            title={mode === "edit" ? "Couldn't save the changes" : "Your invoice wasn't generated"}
                                            description={submit.message}
                                            onClose={() => setSubmit({ status: "idle" })}
                                            data-autotest="invoice-generate-error"
                                        />
                                    )}
                                </FloatingAlert>
                            )}

                            {/* Sticky, full-bleed across form and summary: the
                              * design pins these while scrolling so a mismatch
                              * stays visible no matter how far down the user
                              * is when they hit Generate. Non-blocking and
                              * dismissible; dismissing only hides the banner,
                              * it doesn't resolve anything. */}
                            {(showPremiumMismatch || showPolicyMismatch) && (
                                <div className="bg-secondary sticky top-0 z-20 -mx-4 flex flex-col gap-3 px-4 pb-3 sm:-mx-6 sm:px-6">
                                    {showPremiumMismatch && (
                                        <FeaturedAlert
                                            color="warning"
                                            title="Premium mismatch detected"
                                            description="This invoice's premium doesn't match the amount on the Premium Selection page. You can review it there, or continue, generating this invoice will update the Premium Selection page value."
                                            primaryAction={{
                                                label: "Check Premium Selection page",
                                                onClick: () =>
                                                    navigate(
                                                        `/submissions/${submissionId}/calculations/premium-selection`,
                                                    ),
                                            }}
                                            onClose={() => setDismissedBanners((d) => ({ ...d, premium: true }))}
                                            data-autotest="invoice-premium-mismatch"
                                        />
                                    )}

                                    {showPolicyMismatch && (
                                        <FeaturedAlert
                                            color="warning"
                                            title="Policy number mismatch detected"
                                            description="This invoice's policy number doesn't match the number on Policy Details. Double check which one is correct before generating the invoice."
                                            primaryAction={{
                                                label: "Check Policy Details page",
                                                onClick: () =>
                                                    navigate(`/submissions/${submissionId}/policy/details`),
                                            }}
                                            onClose={() => setDismissedBanners((d) => ({ ...d, policy: true }))}
                                            data-autotest="invoice-policy-mismatch"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Figma's 1440 frame: 68 rail + 24 pad + 780 form
                              * + 24 gap + 520 summary + 24 pad. Pinning the
                              * summary at 520 and letting the form take `1fr`
                              * reproduces 780 exactly at that width, and lets
                              * the form absorb the extra space on a wider
                              * screen instead of leaving a dead gutter. */}
                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_520px]">
                                <div className="flex flex-col gap-6">
                                    <FormGroup title="Brokerage" titleTooltip={INVOICE_CARD_TOOLTIPS.brokerage}>
                                        <Row cols={3}>
                                            <LockedField
                                                label="Brokerage name"
                                                value={prefill.brokerageName}
                                                data-autotest="invoice-brokerage-name"
                                            />
                                            <TextField
                                                readOnly={readOnly}
                                                label="Phone"
                                                isRequired
                                                placeholder="(555) 000-0000"
                                                value={phone}
                                                onChange={edit(setPhone)}
                                                isInvalid={Boolean(err("phone"))}
                                                hint={err("phone")}
                                                data-autotest="invoice-brokerage-phone"
                                            />
                                            <LockedField label="Street" value={prefill.brokerageStreet} />
                                        </Row>
                                        <Row cols={3}>
                                            <LockedField label="City" value={prefill.brokerageCity} />
                                            <LockedField label="State" value={prefill.brokerageState} />
                                            <LockedField label="Zip" value={prefill.brokerageZip} />
                                        </Row>
                                        <Row>
                                            <LockedField label="Submission Type" value={prefill.submissionType} />
                                            {/* Ascend's Account is always agency-billed for Orion —
                                              * the direct-bill fields have no home in the schema. */}
                                            <LockedField label="Billing type" value={BILLING_TYPE_LABEL} />
                                        </Row>
                                    </FormGroup>

                                    <FormGroup title="Billing user" titleTooltip={INVOICE_CARD_TOOLTIPS.billingUser}>
                                        <Row cols={3}>
                                            {readOnly ? (
                                                <LockedField label="Email" value={billingBroker?.email ?? ""} />
                                            ) : (
                                            <Select
                                                size="sm"
                                                label="Email"
                                                isRequired
                                                placeholder="Select email"
                                                items={brokerItems}
                                                selectedKey={billingBrokerId}
                                                onSelectionChange={(k) =>
                                                    edit(selectBillingBroker)(k == null ? null : String(k))
                                                }
                                                isInvalid={Boolean(err("billingBrokerId"))}
                                                hint={err("billingBrokerId")}
                                                data-autotest="invoice-billing-email"
                                            >
                                                {(item: { id: string | number; label?: string }) => (
                                                    <Select.Item id={item.id}>{item.label}</Select.Item>
                                                )}
                                            </Select>
                                            )}
                                            {/* Editable only while creating: once the invoice exists
                                              * the Account User is provisioned in Ascend and no
                                              * endpoint renames them (PATCH is money-only), so Edit
                                              * locks them too. View/Edit show the broker record: the
                                              * invoice GET does not expose the account user it was
                                              * created with. */}
                                            <TextField
                                                readOnly={mode !== "create"}
                                                label="First name"
                                                isRequired
                                                placeholder="Enter first name"
                                                value={mode !== "create" ? (billingBroker?.first_name ?? "") : billingFirstName}
                                                onChange={edit(setBillingFirstName)}
                                                isInvalid={Boolean(err("billingFirstName"))}
                                                hint={err("billingFirstName")}
                                                data-autotest="invoice-billing-first-name"
                                            />
                                            <TextField
                                                readOnly={mode !== "create"}
                                                label="Last name"
                                                isRequired
                                                placeholder="Enter last name"
                                                value={mode !== "create" ? (billingBroker?.last_name ?? "") : billingLastName}
                                                onChange={edit(setBillingLastName)}
                                                isInvalid={Boolean(err("billingLastName"))}
                                                hint={err("billingLastName")}
                                                data-autotest="invoice-billing-last-name"
                                            />
                                        </Row>

                                        {/* A second billing user is not collected: the request
                                          * takes a single `account_user_payload`, so any extra
                                          * contact would be silently dropped (OR-1555). Real
                                          * multi-contact support is a separate follow-up. */}
                                    </FormGroup>

                                    <FormGroup
                                        title="First name insured"
                                        titleTooltip={INVOICE_CARD_TOOLTIPS.firstNamedInsured}
                                    >
                                        <Row>
                                            <LockedField label="FNI" value={prefill.fni} />
                                            <LockedField label="Street" value={prefill.fniStreet} />
                                        </Row>
                                        <Row cols={3}>
                                            <LockedField label="City" value={prefill.fniCity} />
                                            <LockedField label="State" value={prefill.fniState} />
                                            <LockedField label="Zip" value={prefill.fniZip} />
                                        </Row>
                                    </FormGroup>

                                    <FormGroup
                                        title="Invoice Details"
                                        titleTooltip={INVOICE_CARD_TOOLTIPS.invoiceDetails}
                                    >
                                        <Row>
                                            {/* Both land in VARCHAR(255) columns (OR-1655): capped, with the
                                              * counter below in the normal flow like the Save view title. */}
                                            <div>
                                                <TextField
                                                    readOnly={readOnly}
                                                    label="Account manager"
                                                    isRequired
                                                    placeholder="Enter account manager"
                                                    value={accountManager}
                                                    onChange={edit(setAccountManager)}
                                                    isInvalid={Boolean(err("accountManager")) || isAtLimit(accountManager, SUBMISSION_TEXT_MAX_LENGTH)}
                                                    maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                                    data-autotest="invoice-account-manager"
                                                />
                                                {!readOnly && (
                                                    <CharCountHint value={accountManager} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={err("accountManager")} />
                                                )}
                                            </div>
                                            <div>
                                                <TextField
                                                    readOnly={readOnly}
                                                    label="Account email"
                                                    isRequired
                                                    placeholder="Enter email"
                                                    value={accountEmail}
                                                    onChange={edit(setAccountEmail)}
                                                    isInvalid={Boolean(err("accountEmail")) || isAtLimit(accountEmail, SUBMISSION_TEXT_MAX_LENGTH)}
                                                    maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                                    data-autotest="invoice-account-email"
                                                />
                                                {!readOnly && (
                                                    <CharCountHint value={accountEmail} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={err("accountEmail")} />
                                                )}
                                            </div>
                                        </Row>
                                        <Row>
                                            {/* Server-generated `{brokerage}-{sequence}` — never an
                                              * input. View/Edit read it back off the loaded row. */}
                                            <LockedField label="Invoice number" value={invoice?.invoiceNumber ?? ""} />
                                            {/* View/Edit show the invoiced policy number (corrections
                                              * included); Policy Details only seeds a new invoice. */}
                                            <LockedField label="Policy number" value={invoice?.policyNumber ?? prefill.policyNumber} />
                                        </Row>
                                        <Row cols={3}>
                                            {readOnly ? (
                                                /* Same source as the summary: the backend stamps
                                                 * due_date on the row (today + 30) — the local
                                                 * state only matters while the form is editable. */
                                                <LockedField
                                                    label="Due date"
                                                    value={invoice?.dueDate ? isoToUs(invoice.dueDate) : dueDate}
                                                />
                                            ) : (
                                            <DateTextField
                                                size="sm"
                                                label="Due date"
                                                isRequired
                                                defaultValue={dueDate}
                                                onChange={edit(setDueDate)}
                                                isInvalid={Boolean(err("dueDate"))}
                                                hint={err("dueDate")}
                                                data-autotest="invoice-due-date"
                                            />
                                            )}
                                            <LockedField
                                                label="Effective date"
                                                value={invoice?.effectiveDate ? isoToUs(invoice.effectiveDate) : prefill.effectiveDate}
                                            />
                                            <LockedField
                                                label="Expiration date"
                                                value={invoice?.expirationDate ? isoToUs(invoice.expirationDate) : prefill.expirationDate}
                                            />
                                        </Row>
                                        <Row>
                                            {readOnly ? (
                                                <LockedField
                                                    label="Coverage type"
                                                    value={coverageType ? COVERAGE_TYPE_LABELS[coverageType] : ""}
                                                />
                                            ) : (
                                            <Select
                                                size="sm"
                                                label="Coverage type"
                                                isRequired
                                                placeholder="Select coverage type"
                                                items={COVERAGE_ITEMS}
                                                selectedKey={coverageType}
                                                onSelectionChange={(k) =>
                                                    edit(setCoverageType)(
                                                        k == null ? null : (String(k) as AscendCoverageType),
                                                    )
                                                }
                                                isInvalid={Boolean(err("coverageType"))}
                                                hint={err("coverageType")}
                                                data-autotest="invoice-coverage-type"
                                            >
                                                {(item: { id: string | number; label?: string }) => (
                                                    <Select.Item id={item.id}>{item.label}</Select.Item>
                                                )}
                                            </Select>
                                            )}
                                            <LockedField label="Carrier" value={CARRIER_LABEL} />
                                        </Row>
                                    </FormGroup>

                                    <FormGroup
                                        title="Premium & Fees"
                                        titleTooltip={INVOICE_CARD_TOOLTIPS.premiumAndFees}
                                    >
                                        {/* Every figure is the backend's: quote columns,
                                          * displayed read-only. The premium guard runs on
                                          * the same source at Generate time. */}
                                        <Row>
                                            {/* The premium is locked here but still validated
                                              * (Ascend needs it > 0). Without this hint the
                                              * required-fields toast counted an error no field
                                              * showed (OR-1600). */}
                                            <LockedMoney
                                                label="Selected Premium"
                                                value={invoice?.selectedPremium ?? prefill.selectedPremium}
                                                hint={err("premium")}
                                            />
                                            <LockedMoney label="Policy Fee" value={prefill.policyFee} />
                                        </Row>
                                        <Row>
                                            <LockedMoney
                                                label="Taxes"
                                                value={invoice?.taxesTotal ?? prefill.taxesAmount}
                                            />
                                            {/* ⚠️ `surcharges_total` is PCFs only. TRIA, the
                                              * surplus lines fee, MCARE and misc fees are billed
                                              * but land in no visible field, so these parts can
                                              * add up to less than Total invoiced. Raised with
                                              * backend; do NOT paper over it by summing here. */}
                                            <LockedMoney label="Surcharges" value={invoice?.surchargesTotal ?? null} />
                                        </Row>
                                        {/* Captured when the sync completes — a dash before. */}
                                        <TotalRow label="Total invoiced" value={invoice?.totalInvoicedAmount ?? null} />
                                    </FormGroup>

                                    <FormGroup title="Commissions" titleTooltip={INVOICE_CARD_TOOLTIPS.commissions}>
                                        <p className="text-secondary text-sm font-medium">{WHOLESALER_GROUP_LABEL}</p>
                                        {/* The rate comes off the quote; the dollar figure is
                                          * the row's live rollup (rate × selected premium). */}
                                        <Row>
                                            <LockedField
                                                label="Wholesaler / MGA fee"
                                                value={
                                                    invoice?.tricuraCommissionAmount != null
                                                        ? formatMoney(invoice.tricuraCommissionAmount)
                                                        : prefill.tricuraCommissionPct == null
                                                          ? ""
                                                          : `${prefill.tricuraCommissionPct}%`
                                                }
                                            />
                                            <EditableMoney
                                                readOnly={readOnly}
                                                label="Taxes"
                                                value={tricuraTax}
                                                onChange={edit(setTricuraTax)}
                                                autotest="invoice-tricura-tax"
                                            />
                                        </Row>
                                        <div aria-hidden="true" className="border-secondary border-t border-dashed" />
                                        <p className="text-secondary text-sm font-medium">{prefill.fni || "Insured"}</p>
                                        <Row>
                                            <LockedField
                                                label="Commission"
                                                value={
                                                    invoice?.agentCommissionAmount != null
                                                        ? formatMoney(invoice.agentCommissionAmount)
                                                        : prefill.agentCommissionPct == null
                                                          ? ""
                                                          : `${prefill.agentCommissionPct}%`
                                                }
                                            />
                                            <EditableMoney
                                                readOnly={readOnly}
                                                label="Agency fee"
                                                value={agencyFee}
                                                onChange={edit(setAgencyFee)}
                                                autotest="invoice-agency-fee"
                                            />
                                        </Row>
                                        {/* Live while editing so the user sees their own input
                                          * land; the persisted figure is what View reads back. */}
                                        <TotalRow
                                            label="Total commissions"
                                            value={readOnly ? (invoice?.totalCommissionAmount ?? null) : totalCommission}
                                        />
                                    </FormGroup>
                                </div>

                                <InvoiceSummary data={summary} />
                            </div>
                        </div>
                    )}
                </div>

                {/* View has no footer at all: the design makes it purely a
                  * read-back of what was invoiced, with nothing to submit. */}
                {!readOnly && (
                    <footer className="border-secondary bg-primary flex shrink-0 items-center justify-end gap-3 border-t px-4 py-3 sm:px-6">
                        {mode === "edit" ? (
                            <Button
                                size="sm"
                                isDisabled={working || !invoiceId}
                                onClick={onSaveChanges}
                                data-autotest="invoice-save-changes"
                            >
                                {working ? "Saving…" : "Save Changes"}
                            </Button>
                        ) : (
                            <>
                                {/* PUT /invoice-draft. Faithful to the backend:
                                  * the coverage line, the Account choice and
                                  * the three money fields persist — the rest
                                  * re-derives from the submission on read. */}
                                <Button
                                    size="sm"
                                    color="secondary"
                                    isDisabled={loading || draftReadFailed || working || !prefill}
                                    onClick={() => {
                                        void putInvoiceDraft(submissionId, {
                                            coverage_type: coverageType,
                                            ...accountUserPayload(),
                                            ...moneyPayload(),
                                        })
                                            .then(() => navigate(`/submissions/${submissionId}/invoice`))
                                            .catch((e: unknown) =>
                                                setSubmit({
                                                    status: "failed",
                                                    message: e instanceof Error ? e.message : "Couldn't save the draft.",
                                                }),
                                            );
                                    }}
                                    data-autotest="invoice-save"
                                >
                                    Save
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={onGenerateClick}
                                    isDisabled={loading || draftReadFailed || working || !prefill}
                                    data-autotest="invoice-generate"
                                >
                                    {working ? "Generating invoice…" : "Generate Invoice"}
                                </Button>
                            </>
                        )}
                    </footer>
                )}
            </div>

            <WarningDialog
                isOpen={discardOpen}
                onClose={() => setDiscardOpen(false)}
                title="Discard unsaved changes?"
                description="Your changes to this invoice haven't been saved yet."
                actions={
                    <>
                        <Button size="sm" color="secondary" onClick={goInvoices} data-autotest="invoice-discard-confirm">
                            Discard change
                        </Button>
                        <Button size="sm" onClick={() => setDiscardOpen(false)}>
                            Keep editing
                        </Button>
                    </>
                }
            />

            <WarningDialog
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Ready to lock in this invoice?"
                description="This creates the invoice and starts payment tracking. It can't be edited here after this."
                actions={
                    <>
                        <Button size="sm" color="secondary" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" onClick={onConfirmGenerate} data-autotest="invoice-generate-confirm">
                            Generate Invoice
                        </Button>
                    </>
                }
            />
        </div>
    );
}
